import React, { useState } from 'react';
import { format } from 'date-fns';
import { Edit, Eye, Copy, Trash } from 'lucide-react';
import { BlogPost } from '@/services/blog/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface BlogPostsTableProps {
  posts: BlogPost[];
  onEdit: (id: string) => void;
  onView: (slug: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}

export const BlogPostsTable: React.FC<BlogPostsTableProps> = ({
  posts,
  onEdit,
  onView,
  onDuplicate,
  onDelete
}) => {
  const [postToDelete, setPostToDelete] = useState<string | null>(null);

  return (
    <div className="w-full border rounded-md overflow-x-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px]">Title</TableHead>
            <TableHead className="hidden sm:table-cell min-w-[150px]">Slug</TableHead>
            <TableHead className="min-w-[100px]">Status</TableHead>
            <TableHead className="hidden md:table-cell min-w-[120px]">Created</TableHead>
            <TableHead className="text-right min-w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.map((post) => (
            <TableRow key={post.id}>
              <TableCell className="font-medium min-w-[200px]">
                {post.title}
                <div className="sm:hidden text-xs text-neutral-500 mt-1">
                  {post.slug}
                </div>
              </TableCell>
              <TableCell className="text-sm text-neutral-600 hidden sm:table-cell min-w-[150px]">{post.slug}</TableCell>
              <TableCell className="min-w-[100px]">
                <Badge variant={post.published ? 'default' : 'secondary'}>
                  {post.published ? 'Published' : 'Draft'}
                </Badge>
              </TableCell>
              <TableCell className="hidden md:table-cell min-w-[120px]">
                {post.created_at ? format(new Date(post.created_at), 'MMM d, yyyy') : 'N/A'}
              </TableCell>
              <TableCell className="text-right min-w-[100px]">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(post.id)}>
                      <Edit className="mr-2" size={16} />
                      Edit
                    </DropdownMenuItem>
                    
                    {post.published && (
                      <DropdownMenuItem onClick={() => onView(post.slug)}>
                        <Eye className="mr-2" size={16} />
                        View
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuItem onClick={() => onDuplicate(post.id)}>
                      <Copy className="mr-2" size={16} />
                      Duplicate
                    </DropdownMenuItem>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={(e: Event) => {
                            e.preventDefault();
                            setPostToDelete(post.id);
                          }}
                        >
                          <Trash className="mr-2" size={16} />
                          Delete
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the blog post.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                              if (postToDelete) {
                                onDelete(postToDelete);
                                setPostToDelete(null);
                              }
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
